import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { updateUserPreference } from '@kirin/api-client/api/account';
import {
  UserPreferenceKey,
  type TableColumnLayout,
  type UserPreference,
  type UserPreferencesResponse,
} from '@kirin/api-client/contracts/user-preferences';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/hooks/api/queryKeys';
import { toast } from '@/hooks/use-toast';
import { mergePreference } from '@/lib/preferences/mergePreference';
import { selectPreference } from '@/lib/preferences/selectPreference';
import { DEFAULT_COLUMN_LAYOUT } from '@/components/DataTable/utils/columnLayout';
import type { ColumnCustomizationOptions } from '@/components/DataTable/types';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { usePreferencesQuery } from './usePreferencesQuery';

const ALERTS_COLUMNS_MUTATION_KEY = ['preferences', 'alerts_table_columns'];

/**
 * Trailing debounce for the PUT. Each save writes an `audit_logs` row, so a
 * burst of edits (Arrow Down ×10 to move a column, quick toggles) is coalesced
 * into one save of the final layout. The UI updates immediately regardless.
 */
export const ALERTS_COLUMNS_SAVE_DEBOUNCE_MS = 400;

export interface UseAlertsColumnLayoutResult {
  layout: TableColumnLayout;
  setLayout: (layout: TableColumnLayout) => void;
  /**
   * True once the saved layout has loaded. Edits before that (or after the
   * load failed) would overwrite the stored layout with one built on the
   * default, so callers must not offer editing until this is true.
   */
  isReady: boolean;
}

/**
 * Per-user Alerts table column layout (KIR-4089), stored server-side in
 * `user_preferences` so it follows the user across machines and logins.
 *
 * Writes are optimistic: the preferences cache is updated immediately and the
 * PUT runs in the background, debounced (`ALERTS_COLUMNS_SAVE_DEBOUNCE_MS`) and
 * flushed on unmount so leaving the page never drops the last change. Saves
 * share one mutation scope so they reach the server in order (last change
 * wins). On failure the cache is refetched from the server and the user is
 * told the change wasn't saved.
 */
export function useAlertsColumnLayout(): UseAlertsColumnLayoutResult {
  const queryClient = useQueryClient();
  const { data } = usePreferencesQuery();
  // Ready = the server's entry for this key is in the cache. Not the query's
  // `isSuccess`: other preference writers can seed a partial (theme-only)
  // cache that reads as success, and a failed background refetch flips the
  // status to error while keeping good data.
  const savedLayout = selectPreference(
    data,
    UserPreferenceKey.AlertsTableColumns
  );
  const layout = savedLayout ?? DEFAULT_COLUMN_LAYOUT;

  // Debounced edit not yet handed to the mutation (see `setLayout`).
  const pendingRef = useRef<TableColumnLayout | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // True while a newer edit exists than the one settling: queued in the
  // mutation scope (this one still counts as mutating while its callbacks
  // run) or still waiting out the debounce.
  const hasNewerEdit = (): boolean =>
    pendingRef.current !== null ||
    queryClient.isMutating({ mutationKey: ALERTS_COLUMNS_MUTATION_KEY }) > 1;

  const { mutate } = useMutation<UserPreference, Error, TableColumnLayout>({
    mutationKey: ALERTS_COLUMNS_MUTATION_KEY,
    scope: { id: 'preferences:alerts_table_columns' },
    mutationFn: (next: TableColumnLayout) =>
      updateUserPreference(apiClient, {
        key: UserPreferenceKey.AlertsTableColumns,
        value: next,
      }),
    onMutate: async next => {
      // Keep an in-flight GET from overwriting the optimistic write.
      await queryClient.cancelQueries({ queryKey: queryKeys.preferences.all });
      // `setLayout` already wrote this value; re-apply it only to repair a
      // refetch that landed during the debounce — never over a newer edit
      // made while the cancel was awaited.
      if (hasNewerEdit()) return;
      queryClient.setQueryData<UserPreferencesResponse>(
        queryKeys.preferences.all,
        // No cached response = nothing to merge into; writing a partial one
        // would drop the other preferences (theme) from the cache.
        old =>
          old &&
          mergePreference(old, {
            key: UserPreferenceKey.AlertsTableColumns,
            value: next,
          })
      );
    },
    onSuccess: saved => {
      // Reconcile with what the server stored once the last queued edit has
      // saved — an unrelated preferences refetch (e.g. after a theme change)
      // may have landed mid-save and put the old layout back in the cache.
      if (!hasNewerEdit()) {
        queryClient.setQueryData<UserPreferencesResponse>(
          queryKeys.preferences.all,
          old => old && mergePreference(old, saved)
        );
      }
    },
    onError: err => {
      Sentry.captureException(err, {
        level: 'error',
        tags: { source: 'preferences-alerts-columns' },
      });
      toast({
        title: 'Column settings not saved',
        description: 'Your column changes could not be saved. Try again.',
        variant: 'destructive',
      });
      // Resync from the server — unless a newer edit is queued or debouncing:
      // refetching now would visually revert that edit, which saves anyway.
      if (!hasNewerEdit()) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.preferences.all,
        });
      }
    },
    retry: false,
  });

  const flush = useCallback((): void => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    const next = pendingRef.current;
    pendingRef.current = null;
    if (next) mutate(next);
  }, [mutate]);

  // Flush (not drop) a pending save when the table unmounts.
  useEffect(() => flush, [flush]);

  const setLayout = useCallback(
    (next: TableColumnLayout): void => {
      // Stop an in-flight preferences GET (e.g. the refetch after a theme
      // change) from landing inside the debounce window with the old layout.
      // Safe before a synchronous write: a manual setQueryData replaces the
      // cancel's revert state, so the cancel can't undo the optimistic data.
      void queryClient.cancelQueries({ queryKey: queryKeys.preferences.all });
      queryClient.setQueryData<UserPreferencesResponse>(
        queryKeys.preferences.all,
        old =>
          old &&
          mergePreference(old, {
            key: UserPreferenceKey.AlertsTableColumns,
            value: next,
          })
      );
      pendingRef.current = next;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flush, ALERTS_COLUMNS_SAVE_DEBOUNCE_MS);
    },
    [queryClient, flush]
  );

  return { layout, setLayout, isReady: savedLayout !== undefined };
}

/**
 * `DataTable` `columnCustomization` options for the Alerts tables, or
 * `undefined` (today's fixed column set) while the
 * `alerts_column_customization` flag is off or the saved layout has not
 * loaded (still loading, or the load failed — no editing on a guessed base).
 */
export function useAlertsColumnCustomization():
  | ColumnCustomizationOptions
  | undefined {
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.ALERTS_COLUMN_CUSTOMIZATION,
  ]);
  const enabled =
    flags[flagsmithFeatureFlags.ALERTS_COLUMN_CUSTOMIZATION] ?? false;
  const { layout, setLayout, isReady } = useAlertsColumnLayout();
  return useMemo(
    () =>
      enabled && isReady ? { layout, onLayoutChange: setLayout } : undefined,
    [enabled, isReady, layout, setLayout]
  );
}
